'use client'

import {useEffect, useRef, useState} from 'react'
import {usePathname} from 'next/navigation'
import {useReducedMotion, scrollBehavior} from '@/utils/useReducedMotion'

// The visible offset is set globally via `html { scroll-padding-top: ... }` in
// globals.css, so both native hash navigation and `scrollIntoView` respect it.
function scrollToHash(hash: string, behavior: ScrollBehavior) {
  let cancelled = false;
  let attempts = 0;
  const tryScroll = () => {
    if (cancelled) return;
    const el = document.getElementById(hash);
    if (el) {
      // Reset to top instantly so the smooth scroll has somewhere to animate
      // from — on cross-page hash nav, the browser/Next.js otherwise jumps
      // straight to the anchor before this runs.
      window.scrollTo({top: 0, behavior: 'instant'});
      el.scrollIntoView({block: 'start', behavior});
      return;
    }
    if (attempts++ < 120) requestAnimationFrame(tryScroll);
  };
  requestAnimationFrame(tryScroll);

  return () => { cancelled = true; };
}

function getHash() {
  return typeof window !== 'undefined' ? window.location.hash.slice(1) : '';
}

export default function ScrollToTop() {
  const pathname = usePathname();
  const isFirstRunRef = useRef(true);
  const isPopstateRef = useRef(false);
  const reducedMotion = useReducedMotion();
  const [hash, setHash] = useState(getHash);
  // Re-trigger the scroll effect when the user clicks the same hash link
  // twice: hash state stays equal, so without this tick React would bail.
  const [navTick, setNavTick] = useState(0);

  // Keep `hash` synced with the actual URL hash. Native events (`hashchange`,
  // `popstate`) cover anchor clicks and back/forward, but Next.js App Router
  // navigations go through `history.pushState`, which fires neither — so we
  // also patch the history methods to notify us.
  useEffect(() => {
    // The patched history methods can be called from inside React's insertion
    // phase (e.g. router internals), where synchronous setState is forbidden.
    // Defer the state update via microtask to escape that phase.
    const update = () => queueMicrotask(() => {
      const next = getHash();
      setHash(next);
      if (next) setNavTick(t => t + 1);
    });
    const onPopstate = () => {
      isPopstateRef.current = true;
      update();
    };
    update();
    window.addEventListener('hashchange', update);
    window.addEventListener('popstate', onPopstate);

    const origPush = history.pushState;
    const origReplace = history.replaceState;
    history.pushState = function (...args) {
      origPush.apply(this, args);
      update();
    };
    history.replaceState = function (...args) {
      origReplace.apply(this, args);
      update();
    };

    return () => {
      window.removeEventListener('hashchange', update);
      window.removeEventListener('popstate', onPopstate);
      history.pushState = origPush;
      history.replaceState = origReplace;
    };
  }, []);

  useEffect(() => {
    if (isPopstateRef.current) {
      isPopstateRef.current = false;
      return; // browser back/forward — let scroll restoration handle it
    }

    // Read the live URL hash rather than the `hash` state: when navigating to
    // a new path that includes a hash, `pathname` updates before our state
    // microtask fires, and we'd otherwise scroll to top before scrolling to
    // the anchor.
    const behavior = scrollBehavior(reducedMotion);
    const liveHash = getHash();
    if (liveHash) {
      return scrollToHash(liveHash, behavior);
    }

    if (isFirstRunRef.current) {
      isFirstRunRef.current = false;
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      if (nav?.type === 'reload') return; // let the browser keep its restored scroll position
    }

    window.scrollTo({top: 0, behavior});
  }, [pathname, hash, navTick, reducedMotion]);

  return null
}